import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  getCart,
  removeCartItem,
  setCartLineCustomText,
  setCartLinePersonalization,
  storefrontTextCharCount,
  updateCartItemQuantity,
} from '../../utils/chapterSuppliesCart';
import PersonalizationMemberPicker from './PersonalizationMemberPicker';

const formatPrice = (v: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    v || 0
  );

const clipToMaxChars = (val: string, max: number): string =>
  [...val].slice(0, max).join('');

const StorefrontCart: React.FC = () => {
  const navigate = useNavigate();
  const [cart, setCart] = useState(() => getCart());

  const refresh = () => setCart(getCart());

  const subtotal = cart.reduce(
    (sum, line) => sum + line.unit_price * line.quantity,
    0
  );

  return (
    <Box sx={{ p: 3 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/chapter-supplies/storefront')}
        sx={{ mb: 2 }}
      >
        Back to Storefront
      </Button>

      <Typography variant="h4" sx={{ mb: 2 }}>
        My Cart
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/chapter-supplies/storefront/orders')}
        >
          My Orders
        </Button>
      </Box>

      {cart.length === 0 ? (
        <Card>
          <CardContent>
            <Typography>Your cart is currently empty.</Typography>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          {cart.map((line) => {
            const maxLines = Math.max(
              1,
              line.max_text_lines ?? (line.custom_text_lines.length || 1)
            );
            const maxChars = Math.max(1, line.max_text_length ?? 255);
            const linesToShow = line.customization_required
              ? [...line.custom_text_lines]
              : [];
            while (
              line.customization_required &&
              linesToShow.length < maxLines
            ) {
              linesToShow.push('');
            }
            const trimmed = linesForSubmit(linesToShow);

            return (
              <Card key={line.line_id}>
                <CardContent>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'flex-start', md: 'flex-start' }}
                    justifyContent="space-between"
                  >
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="flex-start"
                      sx={{ width: '100%', minWidth: 0 }}
                    >
                      {line.image_url ? (
                        <Box
                          component="img"
                          src={line.image_url}
                          alt={line.name}
                          sx={{
                            width: 80,
                            height: 80,
                            objectFit: 'contain',
                            borderRadius: 1,
                            bgcolor: 'grey.100',
                            p: 0.5,
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            bgcolor: 'grey.100',
                            borderRadius: 1,
                          }}
                        />
                      )}
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{ overflowWrap: 'break-word' }}
                        >
                          {line.name}
                        </Typography>
                        {line.variant_label && (
                          <Chip
                            size="small"
                            label={line.variant_label}
                            variant="outlined"
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        )}
                        <Typography variant="body2" color="text.secondary">
                          {formatPrice(line.unit_price)} each
                        </Typography>
                        {line.order_approval_required && (
                          <Typography
                            variant="caption"
                            color="warning.main"
                            display="block"
                          >
                            Approval required
                          </Typography>
                        )}
                        {line.customization_required &&
                          !line.personalization_required && (
                            <Stack spacing={1} sx={{ mt: 1 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Personalization ({trimmed.length}/{maxLines}{' '}
                                lines used for checkout)
                              </Typography>
                              {linesToShow
                                .slice(0, maxLines)
                                .map((val, idx) => (
                                  <TextField
                                    key={idx}
                                    size="small"
                                    fullWidth
                                    label={`Line ${idx + 1}`}
                                    value={val}
                                    onChange={(e) => {
                                      const clipped = clipToMaxChars(
                                        e.target.value,
                                        maxChars
                                      );
                                      setCartLineCustomText(
                                        line.line_id,
                                        idx,
                                        clipped
                                      );
                                      refresh();
                                    }}
                                    helperText={`${storefrontTextCharCount(val)} / ${maxChars}`}
                                  />
                                ))}
                            </Stack>
                          )}
                        {line.personalization_required && (
                          <Stack spacing={1} sx={{ mt: 1 }}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Member personalization
                            </Typography>
                            <PersonalizationMemberPicker
                              selectedUserID={
                                line.personalization_user_id || null
                              }
                              selectedDisplayName={
                                line.personalization_display_name || null
                              }
                              fullNameValue={
                                line.personalization_full_name || ''
                              }
                              maxChars={maxChars}
                              onChange={(next) => {
                                setCartLinePersonalization(line.line_id, next);
                                refresh();
                              }}
                            />
                          </Stack>
                        )}
                      </Box>
                    </Stack>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{
                        width: { xs: '100%', md: 'auto' },
                        justifyContent: { xs: 'space-between', md: 'flex-end' },
                        flexShrink: 0,
                      }}
                    >
                      <TextField
                        type="number"
                        size="small"
                        label="Qty"
                        value={line.quantity}
                        onChange={(e) => {
                          updateCartItemQuantity(
                            line.line_id,
                            Number(e.target.value)
                          );
                          refresh();
                        }}
                        inputProps={{ min: 0, step: 1 }}
                        sx={{ width: 90 }}
                      />
                      <Typography sx={{ minWidth: 110, textAlign: 'right' }}>
                        {formatPrice(line.unit_price * line.quantity)}
                      </Typography>
                      <IconButton
                        color="error"
                        onClick={() => {
                          removeCartItem(line.line_id);
                          refresh();
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
          <Divider />
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">Subtotal</Typography>
            <Typography variant="h6" color="primary.main">
              {formatPrice(subtotal)}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={() => navigate('/chapter-supplies/storefront/checkout')}
            >
              Checkout
            </Button>
          </Stack>
        </Stack>
      )}
    </Box>
  );
};

function linesForSubmit(lines: string[]): string[] {
  return lines.map((s) => s.trim()).filter(Boolean);
}

export default StorefrontCart;
